(function() {
  if (!window.Pinia || typeof window.Pinia.defineStore !== 'function') {
    console.error('customizerStore.js: Pinia not available. Customizer store will not be created.');
    window.useCustomizerStore = function() {
      console.warn('useCustomizerStore called but Pinia is not available');
      return null;
    };
    return;
  }

  var defineStore = window.Pinia.defineStore;

  /**
   * Utility function to extract property name from Shopify input name format
   */
  window.getPropertyName = function getPropertyName(inputName) {
    var match = inputName.match(/^properties\[(.+)\]$/);
    return match ? match[1] : null;
  };

  /**
   * Pinia store for managing product customizer state
   * Handles text, color, font, and icon customizations
   */
  window.useCustomizerStore = defineStore('customizer', {
  state: () => ({
    /**
     * Holds state for each customization, keyed by property name
     * @type {Object.<string, CustomizationItem>}
     * @example
     * {
     *   'custom-pet-name': { type: 'text', value: 'Buddy', x: 50, y: 50, size: 16, font: 'font-sans', color: '#000000' },
     *   'custom-text-color': { type: 'color', value: '#FF0000', appliesToProperty: 'custom-pet-name' },
     *   'custom-font-style': { type: 'font', value: 'font-serif', appliesToProperty: 'custom-pet-name' },
     *   'custom_icon_1': { type: 'icon', url: '...', x: 50, y: 50, size: 30 }
     * }
     */
    customizations: {},
  }),
  actions: {
    /**
     * Generic action to update or add a customization
     * @param {string} propertyName - The property name to update
     * @param {Object} data - The data to merge with existing customization
     */
    setCustomization(propertyName, data) {
      if (!propertyName) {
        console.warn('setCustomization: propertyName is required');
        return;
      }

      // Ensure the base object exists
      if (!this.customizations[propertyName]) {
        this.customizations[propertyName] = {};
      }

      // Merge new data with existing data for that property
      this.customizations[propertyName] = {
        ...this.customizations[propertyName],
        ...data,
      };
    },

    /**
     * Set text customization value with position and size
     * @param {string} propertyName - The property name
     * @param {string} value - The text value
     * @param {Object} position - Position object with x, y coordinates
     * @param {number} size - Font size
     * @param {number} rotation - Rotation in degrees (0-360)
     * @param {Object} multilineConfig - Multi-line configuration { enabled, charsPerLine, maxTotal }
     * @param {string} alignment - Text alignment ('center', 'top', 'bottom')
     */
    setTextValue(propertyName, value, position, size, rotation, multilineConfig, alignment) {
      // Find related color customization that applies to this text property
      let relatedColor = this.customizations[propertyName]?.color;
      if (!relatedColor) {
        // Search for a color customization that applies to this property
        for (const key in this.customizations) {
          const customization = this.customizations[key];
          if (customization.type === 'color' && customization.appliesToProperty === propertyName) {
            relatedColor = customization.color;
            break;
          }
        }
      }

      // Find related font customization that applies to this text property
      let relatedFont = this.customizations[propertyName]?.font;
      if (!relatedFont) {
        // Search for a font customization that applies to this property
        for (const key in this.customizations) {
          const customization = this.customizations[key];
          if (customization.type === 'font' && customization.appliesToProperty === propertyName) {
            relatedFont = customization.value;
            break;
          }
        }
      }

      const finalColor = relatedColor || '#000000';
      const finalFont = relatedFont || 'font-1';

      this.setCustomization(propertyName, {
        type: 'text',
        value: value || '',
        ...(position && { x: parseFloat(position.x), y: parseFloat(position.y) }),
        ...(size && { size: parseInt(size, 10) }),
        ...(rotation !== undefined && rotation !== null && { rotation: parseFloat(rotation) }),
        ...(multilineConfig && { multilineConfig }),
        ...(alignment && { alignment }),
        // Preserve existing font and color if they exist, or use related values
        font: finalFont,
        color: finalColor,
      });
    },
    /**
     * Set color customization and apply to related text element
     * @param {string} propertyName - The color property name
     * @param {string} value - The color display value
     * @param {string} hex - The hex color code
     * @param {string} appliesToProperty - The property this color applies to
     */
    setColorValue(propertyName, value, hex, appliesToProperty) {
      const validColor = typeof hex === 'string' && hex.startsWith('#') ? hex : '#FF0000';

      this.setCustomization(propertyName, {
        type: 'color',
        value,
        color: hex,
        ...(appliesToProperty && { appliesToProperty }),
      });

      // Also update the color on the related text customization
      const appliesTo = this.customizations[propertyName]?.appliesToProperty;
      if (appliesTo && this.customizations[appliesTo]?.type === 'text') {
        this.setCustomization(appliesTo, { color: validColor });
      }
    },
    /**
     * Set font customization and apply to related text element
     * @param {string} propertyName - The font property name
     * @param {string} value - The font class value
     * @param {string} appliesToProperty - The property this font applies to
     * @param {number} size - The font size
     * @param {number} lineHeight - The line height multiplier for this font (default 1.1)
     * @param {number} fontScale - The font scale multiplier (default 1)
     */
    setFontClass(propertyName, value, appliesToProperty, size, lineHeight, fontScale, fontUrl, fontFamily) {
      const fontValue = value || 'font-sans';
      const fontCustomizationData = {
        type: 'font',
        value: fontValue,
        ...(appliesToProperty && { appliesToProperty }),
        ...(size && { size: parseInt(size, 10) }),
        fontScale: fontScale !== null && fontScale !== undefined ? parseFloat(fontScale) : 1,
        lineHeight: lineHeight !== null && lineHeight !== undefined ? parseFloat(lineHeight) : 1.1,
        // Always explicitly set fontUrl/fontFamily so stale values from
        // a previously selected font are cleared on every switch.
        fontUrl: fontUrl || null,
        fontFamily: fontFamily || null,
        // If this is a text customization, update its font
        ...(this.customizations[propertyName]?.type === 'text' && {
          font: fontValue,
        }),
      };
      console.log('[customizerStore] setFontClass:', propertyName, 'fontFamily:', fontFamily, 'fontUrl:', fontUrl, 'appliesTo:', appliesToProperty, 'data:', fontCustomizationData);

      this.setCustomization(propertyName, fontCustomizationData);

      // Also update the font, size, and line height on the related text customization
      const appliesTo = this.customizations[propertyName]?.appliesToProperty || appliesToProperty;

      if (appliesTo && this.customizations[appliesTo]?.type === 'text') {
        const textCustomization = this.customizations[appliesTo];
        const updatedMultilineConfig = textCustomization.multilineConfig
          ? { ...textCustomization.multilineConfig }
          : null;

        // If multiline is enabled and font has line height, update multilineConfig
        if (updatedMultilineConfig && updatedMultilineConfig.enabled && lineHeight !== null && lineHeight !== undefined) {
          updatedMultilineConfig.lineHeight = parseFloat(lineHeight);
        }

        this.setCustomization(appliesTo, {
          font: fontValue,
          ...(size && { size: parseInt(size, 10) }),
          ...(updatedMultilineConfig && { multilineConfig: updatedMultilineConfig }),
        });
      }
    },
    /**
     * Set icon customization data
     * @param {string} propertyName - The icon property name
     * @param {string} url - The icon URL
     * @param {number} x - X position
     * @param {number} y - Y position
     * @param {number} size - Icon size
     * @param {number} rotation - Rotation in degrees (0-360)
     * @param {string} borderColor - Optional border color for the icon
     */
    setIconData(propertyName, url, x, y, size, rotation, borderColor) {
      this.setCustomization(propertyName, {
        type: 'icon',
        url: url || null,
        x: parseFloat(x) || 50,
        y: parseFloat(y) || 50,
        ...(size && { size: parseInt(size, 10) }),
        ...(rotation !== undefined && rotation !== null && { rotation: parseFloat(rotation) }),
        ...(borderColor && { borderColor: borderColor }),
      });
    },

    /**
     * Initialize store state from DOM elements
     * Preserves existing customizations while syncing with current DOM state
     */
    initializeState() {
      // Don't clear existing state completely - merge with DOM state
      // This preserves customizations when toggling
      const existingCustomizations = { ...this.customizations };

      // Process Color Radios FIRST so text can use the selected color
      document.querySelectorAll('fieldset[data-swatch-group^="color"]').forEach((container) => {
        const appliesToProperty = container.dataset.appliesTo;
        // Adjust selector if needed
        const firstInput = container.querySelector('input[type="radio"]');
        if (!firstInput) return;
        const propName = getPropertyName(firstInput.name);
        if (!propName) return;
        const checkedRadio = container.querySelector('input[type="radio"]:checked');
        if (checkedRadio?.dataset.color) {
          this.setColorValue(propName, checkedRadio.value, checkedRadio.dataset.color, appliesToProperty);
        } else {
          // Fixed: provide proper parameters when no color is selected
          this.setColorValue(propName, 'Default', '#FF0000', appliesToProperty);
        }
      });

      // Process Font Radios SECOND so text can use the selected font
      document.querySelectorAll('fieldset[data-swatch-group^="font"]').forEach((container) => {
        // Adjust selector if needed
        const firstInput = container.querySelector('input[type="radio"]');
        if (!firstInput) return;
        const propName = getPropertyName(firstInput.name);
        if (!propName) return;
        const checkedRadio = container.querySelector('input[type="radio"]:checked');
        const appliesTo = container.dataset.appliesTo;
        // Get size from checked radio, container, or use default
        const size = checkedRadio?.dataset.size
          ? parseInt(checkedRadio.dataset.size, 10)
          : container.dataset.size
          ? parseInt(container.dataset.size, 10)
          : null;
        const fontScale = checkedRadio?.dataset.fontScale ? parseFloat(checkedRadio.dataset.fontScale) : 1;
        const lineHeight = checkedRadio?.dataset.lineHeight ? parseFloat(checkedRadio.dataset.lineHeight) : 1.1;
        const fontUrl = checkedRadio?.dataset.fontUrl || null;
        const fontFamily = checkedRadio?.dataset.fontFamily || null;
        if (checkedRadio?.dataset.class) {
          this.setFontClass(propName, checkedRadio.dataset.class, appliesTo, size, lineHeight, fontScale, fontUrl, fontFamily);
        } else {
          this.setFontClass(propName, 'font-default', appliesTo, size, lineHeight, fontScale, fontUrl, fontFamily); // Default
        }
      });

      // Text Inputs and Textareas - now these will pick up the already-set color and font
      document.querySelectorAll('input[name^="properties["][type="text"], textarea[name^="properties["]').forEach((input) => {
        const propName = getPropertyName(input.name);
        const position = { x: input.dataset.xPosition, y: input.dataset.yPosition };
        const size = input.dataset.size ? parseInt(input.dataset.size, 10) : null;
        const rotation = input.dataset.rotation ? parseFloat(input.dataset.rotation) : 0;
        const multilineConfig = input.dataset.multiline === 'true' ? {
          enabled: true,
          maxCharsPerLine: parseInt(input.dataset.maxCharsPerLine, 10) || 12,
          lineHeight: parseFloat(input.dataset.lineHeight) || 1.2
        } : null;
        const alignment = input.dataset.alignment || 'center';
        if (propName) {
          this.setTextValue(propName, input.value, position, size, rotation, multilineConfig, alignment);
          // Store layout group metadata for text-and-icon blocks
          if (input.dataset.layoutDirection) {
            this.setCustomization(propName, {
              layoutGroup: {
                direction: input.dataset.layoutDirection,
                gap: parseInt(input.dataset.layoutGap, 10) || 0,
                linkedIconProperty: input.dataset.linkedIconProperty,
                iconSize: parseInt(input.dataset.iconSize, 10) || 30,
              },
            });
          }
        }
      });

      // Icon Radios
      document.querySelectorAll('fieldset[data-swatch-group]').forEach((container) => {
        if (!container.querySelector('input[type="radio"][data-icon]')) return;
        const firstInput = container.querySelector('input[type="radio"]');
        if (!firstInput) return;
        const propName = getPropertyName(firstInput.name);
        if (!propName) return;
        const checkedRadio = container.querySelector('input[type="radio"]:checked');
        let url = null,
          x = 50,
          y = 50,
          size = null,
          rotation = 0;
        if (checkedRadio) {
          url = checkedRadio.dataset.icon || null;
          x = checkedRadio.dataset.positionX || 50;
          y = checkedRadio.dataset.positionY || 50;
          size = checkedRadio.dataset.size ? parseInt(checkedRadio.dataset.size, 10) : null;
          rotation = checkedRadio.dataset.rotation ? parseFloat(checkedRadio.dataset.rotation) : 0;
          // Icon found and will be set
        } else {
          // No icon selected for this property
        }
        // Use container default size if no specific size found
        if (!size && container.dataset.size) {
          size = parseInt(container.dataset.size, 10);
        }
        this.setIconData(propName, url, x, y, size, rotation);
      });

      // Merge back any existing customizations that weren't found in DOM
      // This ensures persisted state survives DOM changes
      for (const propName in existingCustomizations) {
        if (!this.customizations[propName]) {
          this.customizations[propName] = existingCustomizations[propName];
        }
      }
    },
  },
  });
})();
